<template>
  <v-card outlined class="block">
    <h3>{{ $t('hedgeOS.dashboard.heatmap.title') }} <span class="meta">{{ $t('hedgeOS.dashboard.heatmap.meta') }}</span></h3>
    <div class="heatmap-grid">
      <div
        v-for="cell in heatmapCells"
        :key="cell.title"
        class="heatmap-cell"
        :style="{ background: cell.background }"
        :title="cell.title"
      ></div>
    </div>

    <h3 style="margin-top:2rem;">{{ $t('hedgeOS.dashboard.heatmap.flowTitle') }} <span class="meta">{{ $t('hedgeOS.dashboard.heatmap.flowMeta') }}</span></h3>
    <table class="mtm-table">
      <thead>
        <tr>
          <th>{{ $t('hedgeOS.dashboard.heatmap.headers.time') }}</th>
          <th>{{ $t('hedgeOS.dashboard.heatmap.headers.client') }}</th>
          <th>{{ $t('hedgeOS.dashboard.heatmap.headers.action') }}</th>
          <th style="text-align:right;">{{ $t('hedgeOS.dashboard.heatmap.headers.amount') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in flowRows" :key="row.time + row.client">
          <td>{{ row.time }}</td>
          <td class="sym">{{ row.client }}</td>
          <td><span class="tag" :class="row.side">{{ $t(`hedgeOS.dashboard.heatmap.actions.${row.actionKey}`) }}</span></td>
          <td style="text-align:right;">{{ row.amount }}</td>
        </tr>
      </tbody>
    </table>
  </v-card>
</template>

<script>
const INTENSITIES = [
  0.2, 0.4, 0.3, 0.8, 0.9, 0.6, 0.3, 0.2, 0.5, 0.7, 0.4, 0.3,
  0.5, 0.7, 0.9, 0.4, 0.3, 0.8, 0.6, 0.5, 0.3, 0.4, 0.2, 0.7,
  0.8, 0.3, 0.5, 0.6, 0.7, 0.9, 0.4, 0.3, 0.8, 0.6, 0.5, 0.4,
  0.3, 0.6, 0.4, 0.7, 0.5, 0.3, 0.8, 0.9, 0.4, 0.2, 0.6, 0.5,
  0.4, 0.5, 0.7, 0.3, 0.6, 0.4, 0.2, 0.5, 0.7, 0.8, 0.3, 0.4,
  0.6, 0.8, 0.5, 0.3, 0.7, 0.9, 0.4, 0.6, 0.3, 0.5, 0.7, 0.4,
];
const SIGNS = [
  1, 1, 1, 1, 1, -1, 1, 1, 1, -1, 1, 1,
  1, 1, 1, -1, -1, 1, 1, 1, 1, 1, -1, 1,
  1, -1, 1, 1, 1, 1, -1, 1, 1, 1, 1, -1,
  1, 1, -1, 1, 1, -1, 1, 1, 1, -1, 1, 1,
  -1, 1, 1, -1, 1, 1, -1, 1, 1, 1, -1, 1,
  1, 1, 1, -1, 1, 1, -1, 1, -1, 1, 1, 1,
];

export default {
  name: "Heatmap",
  data() {
    return {
      flowRows: [
        { time: "14:22", client: "AgroFoods SA", side: "buy", actionKey: "ndfBuy", amount: "USD 2.5M" },
        { time: "13:51", client: "Minerva-Style", side: "sell", actionKey: "ndfSell", amount: "EUR 1.8M" },
        { time: "13:08", client: "AutoParts BR", side: "buy", actionKey: "dolBuy", amount: "200 ct" },
        { time: "11:47", client: "PetroMid", side: "buy", actionKey: "ccsRoll", amount: "USD 15M" },
        { time: "10:14", client: "AluCorp BR", side: "buy", actionKey: "lmeBuy", amount: "500 t" },
        { time: "09:32", client: "CementaSul", side: "sell", actionKey: "ndfSell", amount: "EUR 4M" },
      ],
    };
  },
  computed: {
    heatmapCells() {
      return INTENSITIES.map((intensity, i) => {
        const sign = SIGNS[i];
        const alpha = intensity.toFixed(2);
        return {
          background: sign > 0 ? `rgba(123,196,164,${alpha})` : `rgba(255,77,31,${alpha})`,
          title: this.$t("hedgeOS.dashboard.heatmap.cellTitle", {
            n: i + 1,
            sign: sign > 0 ? "+" : "-",
            pct: (intensity * 100).toFixed(0),
          }),
        };
      });
    },
  },
};
</script>

<style scoped>
.block {
  background: var(--ink);
  padding: 2rem;
}

.block h3 {
  font-family: 'Fraunces', serif;
  font-weight: 600;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-bottom: 1.5rem;
  padding-bottom: 0.8rem;
  border-bottom: 1px solid var(--line);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.block h3 .meta {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.65rem;
  color: var(--muted);
  font-weight: 400;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.heatmap-grid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 2px;
  margin-top: 0.8rem;
}

.heatmap-cell {
  aspect-ratio: 1;
  border: 1px solid rgba(0, 0, 0, 0.2);
}

.mtm-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.75rem;
}

.mtm-table th {
  text-align: left;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.62rem;
  font-weight: 400;
  padding: 0.6rem 0.5rem;
  border-bottom: 1px solid var(--line);
}

.mtm-table td {
  padding: 0.8rem 0.5rem;
  border-bottom: 1px solid var(--line);
}

.mtm-table tr:last-child td {
  border-bottom: none;
}

.mtm-table tr:hover td {
  background: var(--ink2);
}

.mtm-table .sym {
  font-weight: 600;
  color: var(--paper);
}

.tag {
  display: inline-block;
  padding: 2px 6px;
  font-size: 0.58rem;
  border: 1px solid var(--line);
  color: var(--dim);
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.tag.buy {
  border-color: var(--cool);
  color: var(--cool);
}

.tag.sell {
  border-color: var(--hot);
  color: var(--hot);
}
</style>
